import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import { StoreState } from "../redux/reducers";
import {
  fetchSprints,
  addSprint,
  editSprint,
  deleteSprintById,
  fetchSprintsByProject,
} from "../redux/actions/SprintAction";
import Main from "../components/custom/Main";
import PageContainer from "../components/custom/PageContainer";
import Modal from "../components/Modal";
import CollapseCard from "../components/custom/CollapseCard";
import { Sprint } from "../types/Sprint";
import SprintForm from "../components/SprintForm";
import SprintList from "../components/SprintList";
import { useToast } from "../components/toast/ToastProvider";
import SprintFilter from "../components/SprintFilter";

const SprintPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const sprints = useSelector((state: StoreState) => state.sprint);

  const [selected, setSelected] = useState<Sprint | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchSprints()).finally(() => setLoading(false));
  }, [dispatch]);

  const handleSave = async (data: Sprint) => {
    setLoading(true);
    try {
      if (selected) {
        await dispatch(editSprint(data.id, data));
        toast("Sprint updated successfully");
      } else {
        await dispatch(addSprint(data));
        toast("Sprint added successfully");
      }
      await dispatch(fetchSprints());
      setSelected(null);
      setShowModal(false);
    } catch (error) {
      toast("Error saving sprint");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this sprint?")) {
      setLoading(true);
      try {
        await dispatch(deleteSprintById(id));
        toast("Sprint deleted successfully");
        await dispatch(fetchSprints());
      } catch (error) {
        toast("Error deleting sprint");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (item: Sprint) => {
    setSelected(item);
    setShowModal(true);
  };

  const [filters, setFilters] = useState({
    project: "",
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [isFiltered, setIsFiltered] = useState(false);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleFindFilters = () => {
    setAppliedFilters(filters);
    setIsFiltered(true);
    localStorage.setItem("sprintFilter", JSON.stringify(filters));
    if (filters.project) {
      dispatch(fetchSprintsByProject(parseInt(filters.project)));
    } else {
      dispatch(fetchSprints());
    }
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      project: "",
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setIsFiltered(false);
    localStorage.removeItem("sprintFilter");
  };
  useEffect(() => {
    const savedFilters = localStorage.getItem("sprintFilter");
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      setFilters(parsed);
      setAppliedFilters(parsed);
      setIsFiltered(true);
    }
  }, []);

  return (
    <Main>
      <PageContainer
        title="Sprint Management"
        onButtonClick={() => setShowModal(true)}
        buttonText={loading ? "Loading..." : "ADD"}
      >
        <Modal
          visible={showModal}
          size="lg"
          onClose={() => {
            setShowModal(false);
            setSelected(null);
          }}
          headerText={selected ? "Update Sprint" : "Add Sprint"}
        >
          <SprintForm
            initialData={selected}
            onSave={handleSave}
            clearAfterSubmit={() => setSelected(null)}
          />
        </Modal>
        <CollapseCard title="Sprint Filter">
          <SprintFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            onFind={handleFindFilters}
            onReset={handleResetFilters}
          />
        </CollapseCard>
        <CollapseCard title="Sprint List">
          <SprintList
            sprints={sprints.filter(
              (mod) =>
                !appliedFilters.project ||
                mod.projectId.toString() === appliedFilters.project,
            )}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default SprintPage;
